import { APP_VERSION } from '../config.js';
import { clearHardUpdate, getAccessToken, getHardUpdate, setHardUpdate } from '../auth/storage.js';
import { handleExpiredSession, signOutByServerOrder } from '../auth/session.js';
import { restoreScroll } from '../router.js';
import { getChapters, refreshFromMain } from '../study/content.js';
import { chaptersProgress } from '../study/progress.js';
import { pushUserData } from '../study/userData.js';
import { escapeHtml, hashHref } from '../utils/html.js';
import { showBlobImage } from '../utils/blobImage.js';
import { emptyState, errorCard, loaderView, wireRetry } from '../components/feedback.js';
import { logoMark } from '../components/branding.js';
import { ICONS } from '../components/icons.js';
import { renderAppScreen } from '../components/shell.js';

/**
 * The percentages behind everything on this screen — the ring, each card's bar and
 * the tab split — as `chapterId → percent`, filled by {@link loadHome} before the
 * first render.
 *
 * Derived from `topics_done` (`study/progress.js`), not read off the chapter row:
 * nothing persists a percentage any more. Module state for the same reason
 * `activeTab` is — switching tabs re-renders the cards from the list already in
 * hand, and has to reach the same numbers without another store read.
 */
let progressById = new Map();

/** A chapter's percentage as a whole 0–100; anything unknown counts as 0. */
function chapterProgress(chapter) {
  const value = Number(progressById.get(chapter.id));
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, Math.round(value)));
}

const TABS = [
  { id: 'progress', label: 'W trakcie' },
  { id: 'done', label: 'Ukończone' },
];

/**
 * Which tab the chapter list is on — a plain module variable on purpose.
 *
 * It has to outlive the view (going into a chapter and back should land on the
 * same tab), but not the app: like the play bar's resume point, a fresh start
 * begins at the first tab. Nothing here is persisted.
 */
let activeTab = TABS[0].id;

/** A chapter counts as finished at 100% — the only split the tabs make. */
function isChapterDone(chapter) {
  return chapterProgress(chapter) >= 100;
}

/**
 * The summary the ring at the top of the screen shows.
 *
 * `percent` is the **average of the chapters' own percentages** — the same
 * numbers the rows below print, each chapter weighing the same regardless of
 * how many topics it holds. So it moves with every finished topic, not only with
 * a whole finished chapter, and the "X z Y" caption beside it is the coarser
 * count.
 *
 * Announced chapters (`coming_soon`) are in neither: they aren't cached, can't
 * be opened, and would pin the ring below 100% forever.
 */
function chaptersOverview(chapters) {
  const total = chapters.length;
  const done = chapters.filter(isChapterDone).length;
  const sum = chapters.reduce((acc, chapter) => acc + chapterProgress(chapter), 0);
  return { total, done, percent: total ? Math.round(sum / total) : 0 };
}

// Ring geometry: r=28 with a 6px stroke inside a 64px (w-16) box.
const RING_RADIUS = 28;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

/**
 * The circular progress indicator: a pale sand track with a solid sand arc drawn
 * on top by dashing the same circle, and the percentage in the middle.
 *
 * The ring sits on the plain white section, so the track can't be white — it is
 * the arc's own colour at a quarter strength, which keeps the whole ring one hue
 * and lets the arc read as "the filled part of this". The yellow is soft against
 * white by design; the number inside (brand, 7:1) and the caption beside it are
 * what actually carry the value, the ring only reinforces it.
 *
 * The whole `<svg>` is rotated -90° so the arc starts at twelve o'clock. The
 * round cap is dropped at 0% — a zero-length dash with `round` still paints a
 * dot, which reads as "just started" on a chapter list nobody has touched.
 */
function progressRingHtml(percent) {
  const offset = RING_CIRCUMFERENCE * (1 - percent / 100);
  return `
    <svg viewBox="0 0 64 64" class="h-16 w-16 -rotate-90" aria-hidden="true">
      <circle cx="32" cy="32" r="${RING_RADIUS}" fill="none" stroke-width="6" class="stroke-sand/25" />
      <circle cx="32" cy="32" r="${RING_RADIUS}" fill="none" stroke-width="6"
        stroke-linecap="${percent > 0 ? 'round' : 'butt'}"
        stroke-dasharray="${RING_CIRCUMFERENCE.toFixed(2)}"
        stroke-dashoffset="${offset.toFixed(2)}"
        class="stroke-sand" />
    </svg>
    <span class="absolute inset-0 grid place-items-center text-sm font-semibold tabular-nums text-brand">${percent}%</span>`;
}

/**
 * Render (or clear) the progress summary above the server header.
 *
 * Hidden until there is something to count — like "Już wkrótce" and unlike the
 * chapters region, an empty cache (first run, offline, IndexedDB unavailable)
 * gets no ring rather than a "0 z 0" one. It is filled before the chapter list
 * so the page already has this section's height when `restoreScroll()` runs.
 */
function renderProgressSummary(region, chapters) {
  if (!region) return;
  if (!chapters.length) {
    region.hidden = true;
    region.innerHTML = '';
    return;
  }

  const { total, done, percent } = chaptersOverview(chapters);
  // "z" takes the genitive: singular for one chapter, plural for the rest.
  const noun = total === 1 ? 'rozdziału' : 'rozdziałów';

  region.hidden = false;
  region.innerHTML = `
    <div class="flex items-center gap-4 bg-white py-1">
      <div class="relative h-16 w-16 shrink-0"
        role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${percent}"
        aria-label="Twój postęp ${percent}%">
        ${progressRingHtml(percent)}
      </div>
      <div class="min-w-0">
        <h2 class="text-base font-semibold text-neutral-900">Twój postęp</h2>
        <p class="mt-0.5 text-sm leading-snug text-neutral-600">Ukończono ${done} z ${total} ${noun}</p>
      </div>
    </div>`;
}

/**
 * The server's one-word category tag for a chapter (`label`, e.g. "Lekcje"), as a
 * quiet eyebrow chip above the title. `''` when the field is missing — which is
 * also what a chapter cached before the API served it renders, until its
 * `version` moves and `syncChapters` rewrites the row.
 *
 * Lilac keeps it in the brand family without competing with the progress bar, the
 * only saturated colour on the card. `truncate` is insurance only: the contract is
 * a single word.
 */
function chapterLabelChip(chapter) {
  const label = typeof chapter.label === 'string' ? chapter.label.trim() : '';
  if (!label) return '';
  return `
    <span class="mb-1.5 inline-flex max-w-full truncate rounded-full bg-lilac/60 px-2.5 py-0.5 text-xs font-semibold text-brand">
      ${escapeHtml(label)}
    </span>`;
}

/**
 * A chapter list row: the cached cover as a horizontal banner across the top of
 * the card, then the `label` chip, title, description, a completion bar with its
 * percentage along the bottom, and a chevron linking into the chapter.
 *
 * With `comingSoon` the same row is rendered inert — see below.
 */
function chapterCard(chapter, { comingSoon = false } = {}) {
  const labelChip = chapterLabelChip(chapter);
  const description = chapter.description
    ? `<p class="mt-0.5 truncate text-sm leading-snug text-neutral-600">${escapeHtml(chapter.description)}</p>`
    : '';

  // An announced chapter has no content behind it yet: no link, no chevron and
  // no progress bar (a 0% bar on a chapter you can't open reads as broken).
  //
  // Two signals that it isn't openable: the mist tint (vs. the cream of a live
  // card) and a light `opacity-80` on top of it. The tint carries the meaning —
  // the dim alone, at the 60% it used to be, just read as broken rendering.
  if (comingSoon) {
    return `
      <li>
        <div class="flex items-center gap-3 rounded-2xl bg-mist/50 p-4 opacity-60">
          <div class="min-w-0 flex-1">
            ${labelChip}
            <h3 class="text-base font-semibold text-neutral-800">${escapeHtml(chapter.title)}</h3>
            ${description}
          </div>
        </div>
      </li>`;
  }

  const progress = chapterProgress(chapter);

  // The cover is a horizontal strip across the top of the card, clipped to its
  // rounded corners by the link's `overflow-hidden`. Only the openable chapters
  // get one — an announced row returns above, with nothing behind it to picture.
  //
  // Like `topicRow`, a chapter with **no cached Blob** renders no image at all
  // rather than a grey placeholder, so the card reads exactly as it did before
  // covers existed (a swallowed media fetch, or a chapter the API gives none).
  // The `<img>` starts blank: `wireChapterImages` points it at the stored Blob
  // once the card is in the DOM, since the `image` path needs the access token
  // and can never be an `<img src>`. The fixed height keeps the card's layout
  // independent of when (or whether) the Blob loads.
  const cover =
    chapter.imageBlob instanceof Blob
      ? `<img data-chapter-image="${escapeHtml(chapter.id)}" alt="" loading="lazy"
            class="h-24 w-full bg-mist object-cover" />`
      : '';

  return `
    <li>
      <a href="${hashHref('/chapter/{}', chapter.id)}"
        class="block overflow-hidden rounded-2xl bg-cream transition active:bg-sand/25">
        ${cover}
        <div class="flex items-center gap-3 px-4 py-4">
          <div class="min-w-0 flex-1">
            ${labelChip}
            <h2 class="text-base font-semibold text-neutral-900">${escapeHtml(chapter.title)}</h2>
            ${description}
            <div class="mt-3 flex items-center gap-2.5">
              <div class="h-1.5 flex-1 overflow-hidden rounded-full bg-lilac"
                role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${progress}" aria-label="Postęp ${progress}%">
                <div class="h-full rounded-full bg-brand" style="width: ${progress}%"></div>
              </div>
              <span class="shrink-0 text-xs font-semibold tabular-nums text-brand">${progress}%</span>
            </div>
          </div>
          <span class="shrink-0 text-brand/40">${ICONS.chevronRight()}</span>
        </div>
      </a>
    </li>`;
}

/**
 * Point each chapter card's cover `<img>` at its cached Blob. Read only from
 * IndexedDB — the original proxy URL is never requested at render time (it needs
 * the Bearer token).
 *
 * Has to run after every render of the cards, i.e. on load **and** on each tab
 * switch, which rebuilds the panel's markup from scratch.
 */
function wireChapterImages(container, chapters) {
  const byId = new Map(chapters.map((chapter) => [chapter.id, chapter]));
  container.querySelectorAll('img[data-chapter-image]').forEach((img) => {
    showBlobImage(img, byId.get(img.getAttribute('data-chapter-image'))?.imageBlob);
  });
}

/** The chapters belonging to a tab, in the order the store gave them. */
function chaptersForTab(chapters, tab) {
  return chapters.filter((chapter) => isChapterDone(chapter) === (tab === 'done'));
}

/**
 * The two-tab switcher above the list: a full-width segmented control, one half
 * each, the active one filled with the brand colour. Counts are baked into the
 * labels, so the bar is rendered once per load and only its selected state moves
 * afterwards.
 *
 * A filled pill rather than the underline it used to be: the underline was the
 * one border left on this screen, and a tinted track carries the same "pick one
 * of two" affordance without drawing a line.
 */
function tabsHtml(chapters) {
  const buttons = TABS.map((tab) => {
    const selected = tab.id === activeTab;
    const count = chaptersForTab(chapters, tab.id).length;
    return `
      <button type="button" role="tab" id="chapters-tab-${tab.id}" data-tab="${tab.id}"
        aria-selected="${selected}" aria-controls="chapters-panel" tabindex="${selected ? '0' : '-1'}"
        class="w-1/2 rounded-full px-3 py-2 text-sm font-semibold transition ${
          selected ? 'bg-brand text-white' : 'text-neutral-600'
        }">
        ${tab.label} (${count})
      </button>`;
  }).join('');

  return `<div role="tablist" aria-label="Rozdziały" class="flex gap-1 rounded-full bg-lilac/50 p-1">${buttons}</div>`;
}

/** What a tab shows when the split leaves it with nothing — one copy per tab. */
function emptyTabHtml(tab) {
  return tab === 'done'
    ? emptyState('Brak ukończonych rozdziałów')
    : emptyState('Wszystko ukończone', 'Nie masz rozdziałów w trakcie — świetna robota!');
}

/** The list body for one tab: its rows, or that tab's empty state. */
function tabPanelHtml(chapters, tab) {
  const rows = chaptersForTab(chapters, tab);
  if (!rows.length) return emptyTabHtml(tab);
  return `<ul class="flex flex-col gap-4">${rows.map((chapter) => chapterCard(chapter)).join('')}</ul>`;
}

/** Markup for the chapters region given the cached list and any refresh error. */
function chaptersRegion(chapters, refreshError) {
  if (!chapters.length) {
    if (refreshError) return errorCard(refreshError, 'Nie udało się wczytać rozdziałów.');
    return emptyState('Brak rozdziałów', 'Treści pojawią się tutaj po synchronizacji.');
  }

  // Chapters are cached — show them, and flag if the latest refresh didn't reach the server.
  const staleBanner = refreshError
    ? `<p class="mb-4 rounded-2xl bg-sand/40 px-4 py-3 text-xs leading-snug text-neutral-800">
         Nie udało się odświeżyć treści. Pokazujemy zapisaną wersję.
       </p>`
    : '';

  return `
    ${staleBanner}
    ${tabsHtml(chapters)}
    <div id="chapters-panel" role="tabpanel" aria-labelledby="chapters-tab-${activeTab}" class="mt-6">
      ${tabPanelHtml(chapters, activeTab)}
    </div>`;
}

/**
 * Wire the tab bar: switching only re-renders the panel from the list we
 * already have — no refetch, and the counts in the labels can't change.
 *
 * Left/right arrows move between the tabs (automatic activation), matching the
 * usual tablist behaviour; `activeTab` is module state, so the choice survives
 * navigating into a chapter and back.
 */
function wireTabs(region, chapters) {
  const tabs = [...region.querySelectorAll('[role="tab"]')];
  const panel = region.querySelector('#chapters-panel');
  if (!tabs.length || !panel) return;

  const select = (tab) => {
    if (tab === activeTab) return;
    activeTab = tab;
    tabs.forEach((button) => {
      const selected = button.dataset.tab === activeTab;
      button.setAttribute('aria-selected', String(selected));
      button.tabIndex = selected ? 0 : -1;
      button.classList.toggle('bg-brand', selected);
      button.classList.toggle('text-white', selected);
      button.classList.toggle('text-neutral-600', !selected);
    });
    panel.setAttribute('aria-labelledby', `chapters-tab-${activeTab}`);
    panel.innerHTML = tabPanelHtml(chapters, activeTab);
    wireChapterImages(panel, chapters); // fresh markup → fresh covers
  };

  tabs.forEach((button, index) => {
    button.addEventListener('click', () => select(button.dataset.tab));
    button.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      const step = event.key === 'ArrowRight' ? 1 : -1;
      const next = tabs[(index + step + tabs.length) % tabs.length];
      select(next.dataset.tab);
      next.focus();
    });
  });
}

/**
 * The three server-authored copy blocks, by field name: the page heading, the
 * announcement and the quiet note. Only their styling differs — see
 * {@link renderRichRegion}, which is the single place the raw HTML enters the DOM.
 *
 * One hue each, so the three never read as the same kind of message: the heading
 * is plain text, `alert` sits on sand (the app's "pay attention" tint) and `info`
 * on mist (a quiet aside). Links are always brand.
 */
const RICH_REGIONS = {
  header:
    '<div class="text-2xl font-semibold tracking-tight text-neutral-900 [&_a]:text-brand [&_a]:underline">',
  alert:
    '<div role="status" class="rounded-2xl bg-sand/50 px-5 py-4 text-sm leading-snug text-neutral-900 [&_a]:font-semibold [&_a]:text-brand [&_a]:underline">',
  info: '<div class="rounded-2xl bg-mist/45 px-4 py-3 text-sm leading-snug text-neutral-700 [&_a]:font-semibold [&_a]:text-brand [&_a]:underline">',
};

/**
 * Render (or clear) one of the server-provided copy blocks (`header`, `alert`,
 * `info`) from `/study/main`.
 *
 * ⚠ These are the **only** raw-HTML surfaces in the app: the copy is authored on
 * the backend and may legitimately carry markup (a link in an update notice), so it
 * is injected without `escapeHtml()` — the one deliberate exception to the rule
 * that every dynamic value is escaped. It holds only as long as nothing
 * user-controlled can reach these fields; a compromised API response would run
 * script in the app's origin. Kept in one function precisely so this decision has
 * exactly one place to be revisited (e.g. to add an allow-list sanitizer).
 *
 * @param {?HTMLElement} region Section to fill; hidden when there is nothing to show.
 * @param {?string} html Server copy, or a falsy value to clear the region.
 * @param {'header'|'alert'|'info'} kind Which block's styling to use.
 */
function renderRichRegion(region, html, kind) {
  if (!region) return;
  const copy = typeof html === 'string' ? html.trim() : '';
  if (!copy) {
    region.hidden = true;
    region.innerHTML = '';
    return;
  }
  region.hidden = false;
  region.innerHTML = `${RICH_REGIONS[kind]}${copy}</div>`;
}

/**
 * The announced-but-not-yet-available chapters from the `/study/main` payload,
 * ordered by `position` like the live ones. `[]` when the field is missing.
 *
 * Note the field name: the backend spells it `coming_soon`.
 */
function comingSoonChapters(payload) {
  const list = payload?.coming_soon;
  if (!Array.isArray(list)) return [];
  return [...list].sort((a, b) => a.position - b.position);
}

/**
 * Render (or clear) the "Już wkrótce" list beneath the chapters.
 *
 * Read straight from the payload — like `header` / `alert` / `info` and unlike
 * the chapters — because these rows have no content to cache and nothing to
 * open: the section simply stays hidden when a refresh doesn't reach the server.
 */
function renderComingSoon(region, chapters) {
  if (!region) return;
  if (!chapters.length) {
    region.hidden = true;
    region.innerHTML = '';
    return;
  }
  region.hidden = false;
  region.innerHTML = `
    <h2 class="text-lg font-semibold tracking-tight text-neutral-900">Już wkrótce</h2>
    <ul class="mt-4 flex flex-col gap-4">
      ${chapters.map((chapter) => chapterCard(chapter, { comingSoon: true })).join('')}
    </ul>`;
}

/* -------------------------------------------------------------------------- */
/* Required update ("hardUpdate")                                             */
/* -------------------------------------------------------------------------- */

/** An `http(s)` URL, or '' if the value isn't a safe absolute link. */
function safeUrl(url) {
  const value = typeof url === 'string' ? url.trim() : '';
  return /^https?:\/\//i.test(value) ? value : '';
}

/**
 * The persisted form of the server's `hardUpdate` notice — the copy the gate
 * shows, plus the two fields that make it work when `/study/main` can't be
 * reached.
 *
 * `appVersion` records **which build** was blocked, and it is the only way back
 * out: localStorage survives an app update, so a bare flag would gate the
 * updated app forever. A record tagged with another version is stale by
 * definition (the update it demanded has happened) and is dropped on sight —
 * which works offline, unlike waiting for a clean response to lift it.
 *
 * `backedUp` marks that the user's own rows reached the account, so the push
 * behind the gate happens once rather than on every launch.
 */
function hardUpdateRecord(info) {
  const text = (value) => (typeof value === 'string' ? value.trim() : '');
  return {
    appVersion: APP_VERSION,
    title: text(info?.title),
    description: text(info?.description),
    link: text(info?.link),
    linkText: text(info?.linkText),
    backedUp: false,
  };
}

/** The recorded block, or `null` when this build isn't the one that was gated. */
function readHardUpdate() {
  const record = getHardUpdate();
  if (!record) return null;
  if (record.appVersion !== APP_VERSION) {
    clearHardUpdate();
    return null;
  }
  return record;
}

/**
 * Remember a block the server just announced, keeping a push that already
 * succeeded — the notice may well be re-served on every launch, and the data
 * only has to travel once.
 */
function noteHardUpdate(info) {
  const record = { ...hardUpdateRecord(info), backedUp: Boolean(readHardUpdate()?.backedUp) };
  setHardUpdate(record);
  return record;
}

/**
 * Hand this device's favorites, dictionary and completions to the account before
 * the update — the same document sign-out pushes (`pushUserData`), and **only**
 * the push: nothing is cleared here. The device can sit on the blocked build for
 * a while (the store link can fail, the user can put it off), and those rows are
 * what they come back to; clearing them would leave a screen the user can't even
 * reach to fix.
 *
 * Once per recorded block, marked in storage. A failed push (offline, 413) leaves
 * the mark off, so the next launch behind the gate tries again — which is exactly
 * why this also runs on the offline path, where it simply fails again until there
 * is a connection. An empty device sends nothing and counts as done.
 *
 * Silent either way: it is a best-effort save behind a screen with no controls,
 * so there is nothing the user could do about a failure.
 */
async function backupForHardUpdate(record) {
  if (record.backedUp) return;
  const jwt = getAccessToken();
  if (!jwt) return; // no token names no account — nothing to push to
  try {
    await pushUserData(jwt);
  } catch {
    return; // unmarked → retried on the next launch
  }
  setHardUpdate({ ...record, backedUp: true });
}

/**
 * Re-assert the globals the gate owns.
 *
 * The inverse of every other overlay here, which releases its globals on
 * `hashchange` (see CLAUDE.md): this one is meant to outlive navigation. A
 * browser Back behind the gate re-runs a route, and `renderAppScreen`
 * defensively drops `overflow-hidden` on mount — which would leave the page
 * scrollable underneath. Registered as a named function so wiring it twice is a
 * no-op.
 */
function lockBehindHardUpdate() {
  document.documentElement.classList.add('overflow-hidden');
  document.getElementById('app')?.setAttribute('inert', '');
}

/**
 * Full-screen, blocking "update required" gate. When the server marks a
 * `hardUpdate`, the app is unusable on this version, so we cover everything
 * (the shell chrome included) and offer only the store link. The rest of the
 * home screen is deliberately not rendered.
 *
 * Structured fields, so escaped as text — unlike `alert`/`info`, this is not a
 * raw-HTML surface.
 */
function renderHardUpdate({ title, description, link, linkText } = {}) {
  // Defensive: never stack two gates if this somehow runs twice.
  document.getElementById('hard-update-gate')?.remove();

  const href = safeUrl(link);
  const descriptionHtml = description
    ? `<p class="mt-2 text-sm leading-relaxed text-neutral-600">${escapeHtml(description)}</p>`
    : '';
  const button = href
    ? `<a href="${escapeHtml(href)}" rel="noopener noreferrer" class="btn-primary mt-8 w-full px-4 py-3.5">
        ${escapeHtml(linkText || 'Zaktualizuj teraz')}
      </a>`
    : '';

  const gate = document.createElement('div');
  gate.id = 'hard-update-gate';
  gate.setAttribute('role', 'alertdialog');
  gate.setAttribute('aria-modal', 'true');
  gate.setAttribute('aria-labelledby', 'hard-update-title');
  gate.className =
    'fixed inset-0 z-[70] flex min-h-dvh flex-col items-center justify-center bg-cream px-8 text-center';
  gate.innerHTML = `
    <div class="flex w-full max-w-sm flex-col items-center">
      ${logoMark('text-2xl')}
      <div class="mt-8 grid h-14 w-14 place-items-center rounded-2xl bg-lilac text-brand">
        ${ICONS.download()}
      </div>
      <h1 id="hard-update-title" class="mt-5 text-xl font-semibold tracking-tight text-neutral-900">
        ${escapeHtml(title || 'Wymagana aktualizacja')}
      </h1>
      ${descriptionHtml}
      ${button}
    </div>`;

  // Lock scroll and make the app behind the gate inert (no stray focus / taps),
  // and keep doing so if a route ever renders underneath.
  lockBehindHardUpdate();
  window.addEventListener('hashchange', lockBehindHardUpdate);
  document.body.appendChild(gate);
}

/**
 * Raise the gate from what was stored on an earlier run, if this build is the
 * blocked one.
 *
 * Called by `startApp()` **before** the router, so a required update covers every
 * route rather than only home — a deep link straight into `#/topic/5`, or any
 * screen the user was last on, is gated too. It is the whole reason the notice is
 * persisted: `/study/main` is the only thing that knows about a required update,
 * so an offline launch would otherwise show the cached chapters as if nothing
 * were wrong.
 *
 * @returns {boolean} `true` when the app is gated and must not start.
 */
export function showHardUpdateGate() {
  const record = readHardUpdate();
  if (!record) return false;
  renderHardUpdate(record);
  void backupForHardUpdate(record); // retry, if it never landed
  return true;
}

/**
 * Fetch `/study/main`, reconcile into IndexedDB, then render the server header,
 * the alert, the info note and the chapters from the local cache. Best-effort:
 * a failed refresh still shows cached content.
 */
async function loadHome(mount) {
  const progressRegion = mount.querySelector('#progress-region');
  const headerRegion = mount.querySelector('#header-region');
  const alertRegion = mount.querySelector('#alert-region');
  const infoRegion = mount.querySelector('#info-region');
  const comingSoonRegion = mount.querySelector('#coming-soon-region');
  const chaptersEl = mount.querySelector('#chapters-region');
  chaptersEl.innerHTML = loaderView('Pobieranie treści…', { fullscreen: false });

  let refreshError = null;
  let payload = null;
  try {
    payload = await refreshFromMain(getAccessToken());
  } catch (error) {
    if (handleExpiredSession(error)) return;
    // Offline / server error → fall back to whatever is cached.
    refreshError = error;
  }

  // Required update → record the block (so it holds on the next launch, network or
  // not), hand the user's data to the account, and show nothing but the prompt.
  //
  // Checked **before** `hardLogout`: a gated build can't be used at all, so there is
  // nothing for a session to do behind the gate, and this branch's own push already
  // commits the user's rows to the account. A response carrying both flags therefore
  // signs the user out on the first `/study/main` the *updated* build makes.
  if (payload?.hardUpdate) {
    const record = noteHardUpdate(payload.hardUpdate);
    renderHardUpdate(record);
    void backupForHardUpdate(record);
    return;
  }

  // Server-ordered sign-out → back the user's data up to the account, drop the
  // session and leave for `/login`. Nothing is stored about it: the flag is a
  // one-shot order (see `signOutByServerOrder`), so there is no gate to persist and
  // no state to clear later — the next `/study/main` decides again.
  if (payload?.hardLogout === true) {
    await signOutByServerOrder();
    return;
  }

  renderRichRegion(headerRegion, payload?.header, 'header');
  renderRichRegion(alertRegion, payload?.alert, 'alert');
  renderRichRegion(infoRegion, payload?.info, 'info');
  // Filled in before the chapters list, so the page already has this section's
  // height by the time `restoreScroll()` runs.
  renderComingSoon(comingSoonRegion, comingSoonChapters(payload));

  let chapters = [];
  try {
    chapters = await getChapters();
  } catch {
    /* IndexedDB unavailable (private mode / disabled) — treat as empty */
  }

  // The percentages are counted from `topics_done` here, once, and every render
  // below (including a later tab switch) reads the map. A failed count is not a
  // failed screen: the chapters still list, all reading 0%.
  try {
    progressById = await chaptersProgress(chapters.map((chapter) => chapter.id));
  } catch {
    progressById = new Map();
  }

  renderProgressSummary(progressRegion, chapters);
  chaptersEl.innerHTML = chaptersRegion(chapters, refreshError);
  wireChapterImages(chaptersEl, chapters);
  wireTabs(chaptersEl, chapters);
  restoreScroll(); // the list is in the DOM — return to where the user left it

  wireRetry(chaptersEl, () => loadHome(mount));
}

/** Authenticated landing screen — study chapters synced from `/study/main`. */
export function renderHome(mount) {
  const content = `
    <div class="flex flex-1 flex-col">
      <section id="progress-region" hidden class="mb-6"></section>
      <section id="header-region" hidden></section>
      <section id="info-region" hidden class="mt-4"></section>
      <section id="alert-region" hidden class="mt-4"></section>
      <section id="chapters-region" class="mt-6"></section>
      <section id="coming-soon-region" hidden class="mt-8"></section>
    </div>`;

  renderAppScreen(mount, {
    active: '',
    contentHtml: content,
    leading: logoMark(),
    onMount(main) {
      // Fetch + cache + render the alert and chapters on every visit to home.
      loadHome(main);
    },
  });
}
